import React, { useState } from 'react';
import { FaPlus, FaTimes, FaEdit, FaCheck } from 'react-icons/fa';

/**
 * Environment Variables Editor
 * - Add/remove/edit environment variables
 * - Variable name, description, required flag
 */
const EnvVarEditor = ({ environmentVars, onChange }) => {
  const [editingVar, setEditingVar] = useState(null);
  const [newVarName, setNewVarName] = useState('');
  const [newVarDescription, setNewVarDescription] = useState('');
  const [newVarRequired, setNewVarRequired] = useState(true);

  const addVariable = () => {
    const name = newVarName.trim().toUpperCase();
    if (!name) {
      alert('Please enter a variable name');
      return;
    }

    if (environmentVars[name]) {
      alert('This variable already exists');
      return;
    }

    onChange({
      ...environmentVars,
      [name]: {
        description: newVarDescription.trim() || '',
        required: newVarRequired,
      },
    });

    // Reset form
    setNewVarName('');
    setNewVarDescription('');
    setNewVarRequired(true);
  };

  const removeVariable = (name) => {
    const newVars = { ...environmentVars };
    delete newVars[name];
    onChange(newVars);
  };

  const updateVariable = (name, updates) => {
    onChange({
      ...environmentVars,
      [name]: {
        ...environmentVars[name],
        ...updates,
      },
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addVariable();
    }
  };

  return (
    <div className="space-y-4">
      {/* Existing Variables */}
      {Object.keys(environmentVars).length > 0 && (
        <div className="space-y-2">
          {Object.entries(environmentVars).map(([name, config]) => (
            <div
              key={name}
              className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-spooky-bg-medium"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <code className="font-mono font-semibold text-cyan-600 dark:text-cyan-400">
                    {name}
                  </code>
                  {config.required && (
                    <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 text-xs rounded-full">
                      Required
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeVariable(name)}
                  className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 p-1 rounded transition"
                >
                  <FaTimes />
                </button>
              </div>

              {editingVar === name ? (
                <div className="space-y-2">
                  <textarea
                    value={config.description}
                    onChange={(e) => updateVariable(name, { description: e.target.value })}
                    placeholder="Description..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:bg-spooky-bg-light dark:text-spooky-ghost-white"
                    rows={2}
                  />
                  <div className="flex items-center gap-2">
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={config.required}
                        onChange={(e) => updateVariable(name, { required: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-gray-700 dark:text-gray-300">Required</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setEditingVar(null)}
                      className="ml-auto px-3 py-1 bg-cyan-500 text-white text-xs rounded hover:bg-cyan-600 transition inline-flex items-center gap-1"
                    >
                      <FaCheck />
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {config.description || <em>No description</em>}
                  </p>
                  <button
                    type="button"
                    onClick={() => setEditingVar(name)}
                    className="text-cyan-500 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 p-1 rounded transition ml-2"
                  >
                    <FaEdit />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add New Variable */}
      <div className="p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1 text-gray-700 dark:text-gray-300">
              Variable Name
            </label>
            <input
              type="text"
              value={newVarName}
              onChange={(e) => setNewVarName(e.target.value.toUpperCase())}
              onKeyPress={handleKeyPress}
              placeholder="e.g., API_KEY"
              className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:bg-spooky-bg-medium dark:text-spooky-ghost-white"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1 text-gray-700 dark:text-gray-300">
              Description
            </label>
            <textarea
              value={newVarDescription}
              onChange={(e) => setNewVarDescription(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="What is this variable used for?"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:bg-spooky-bg-medium dark:text-spooky-ghost-white"
              rows={2}
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center text-sm">
              <input
                type="checkbox"
                checked={newVarRequired}
                onChange={(e) => setNewVarRequired(e.target.checked)}
                className="mr-2"
              />
              <span className="text-gray-700 dark:text-gray-300">Required</span>
            </label>

            <button
              type="button"
              onClick={addVariable}
              disabled={!newVarName.trim()}
              className="px-4 py-2 bg-cyan-500 text-white rounded hover:bg-cyan-600 transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 text-sm"
            >
              <FaPlus />
              Add Variable
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvVarEditor;
